import { useToasts, useToast, type ToastVariant } from '@/contexts/ToastContext';

const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; color: string }> = {
  success: {
    bg: 'var(--bg-success-subtle, rgba(34, 197, 94, 0.1))',
    border: 'var(--color-success-600, #16a34a)',
    color: 'var(--color-success-600, #16a34a)',
  },
  error: {
    bg: 'var(--bg-error-subtle, rgba(239, 68, 68, 0.1))',
    border: 'var(--color-error-600, #dc2626)',
    color: 'var(--color-error-600, #dc2626)',
  },
  info: {
    bg: 'var(--bg-surface, rgba(59, 130, 246, 0.1))',
    border: 'var(--border-default, #3b82f6)',
    color: 'var(--text-primary, #e5e7eb)',
  },
};

const VARIANT_ICONS: Record<ToastVariant, string> = {
  success: '\u2713',
  error: '!',
  info: 'i',
};

export const ToastContainer = () => {
  const toasts = useToasts();
  const { dismiss } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 'var(--space-4, 16px)',
        right: 'var(--space-4, 16px)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2, 8px)',
        pointerEvents: 'none',
        maxWidth: '380px',
        width: '100%',
      }}
    >
      {toasts.map((toast) => {
        const style = VARIANT_STYLES[toast.variant];
        return (
          <div
            key={toast.id}
            role="alert"
            style={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3, 12px)',
              padding: 'var(--space-3, 12px) var(--space-4, 16px)',
              borderRadius: 'var(--radius-md, 8px)',
              backgroundColor: style.bg,
              borderLeft: `3px solid ${style.border}`,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              fontSize: 'var(--text-sm, 14px)',
              color: 'var(--text-primary, #e5e7eb)',
              animation: toast.dismissing
                ? 'toast-out 0.3s ease-in forwards'
                : 'toast-in 0.3s ease-out',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'var(--font-weight-bold, 700)',
                backgroundColor: style.border,
                color: '#fff',
              }}
            >
              {VARIANT_ICONS[toast.variant]}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
              style={{
                flexShrink: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-tertiary)',
                fontSize: '16px',
                padding: '2px',
                lineHeight: 1,
              }}
            >
              x
            </button>
          </div>
        );
      })}
    </div>
  );
};
